import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import * as React from 'react';

import type { ChangeRequestMessagesProps } from './ChangeRequestMessages.types';

import style from './ChangeRequestMessages.module.css';

const ChangeRequestMessages = React.memo(
  ({
    isSuccess,
    isError,
    onCreateAnother,
  }: Readonly<ChangeRequestMessagesProps>): React.ReactElement => {
    const handleCreateAnother = React.useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onCreateAnother();
      },
      [onCreateAnother]
    );

    return (
      <div
        data-testid="change-request-messages"
        className={style.changeRequestMessages}
      >
        {isError && (
          <CobaltSectionMessage
            variant="danger"
            sectionMessageTitle={
              <h3>
                Request submission failed. Try again or{' '}
                <CobaltLink
                  href={import.meta.env.VITE_HELP_HOME_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contact customer support
                </CobaltLink>
              </h3>
            }
          />
        )}

        {!isSuccess && (
          <CobaltSectionMessage
            variant="info"
            sectionMessageTitle={
              <h3>
                Our team will get back to you within 2 business days with a
                follow-up on your request. Note that requests are subject to
                change depending on availability, feasibility and other factors.
              </h3>
            }
          />
        )}

        {isSuccess && (
          <CobaltSectionMessage
            variant="success"
            sectionMessageTitle={<h3>Request sent successfully.</h3>}
            sectionMessageDetails={
              <p className="body-02">
                Our team will get back to you within 2 business days with a
                follow-up on your request. Note that requests are subject to
                change depending on availability, feasibility and other factors.
              </p>
            }
            sectionMessageAction={
              <CobaltLink onClick={handleCreateAnother}>
                Create another request?
              </CobaltLink>
            }
          />
        )}
      </div>
    );
  }
);

ChangeRequestMessages.displayName = 'ChangeRequestMessages';

export { ChangeRequestMessages };
